'use client'

import {
  authFieldClassName,
  authFormFieldClass,
  authFormHeaderClass,
  authFormStackClass,
  authLabelClassName,
  authLargeControlClassName,
  authPrimaryButtonClassName,
} from '@/components/pages/auth/libs/auth-form-styles'
import { AuthDivider } from '@/components/pages/auth/ui/AuthDivider'
import { AuthShell } from '@/components/pages/auth/ui/AuthShell'
import { OtpCodeInput } from '@/components/pages/auth/ui/OtpCodeInput'
import { PROVIDER_NAMES, SocialLoginButtons } from '@/components/pages/auth/ui/SocialLoginButtons'
import { Button } from '@/components/ui/button'
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import {
  clearPendingSignIn,
  getPendingSignIn,
  type PendingSignIn,
  sendSignInEmail,
  SignInCodeError,
  type SignInErrorReason,
  submitSignInCode,
} from '@/lib/auth/kratos-code-login'
import { readSocialRedirect, type SocialProvider, type SocialRedirect } from '@/lib/auth/kratos-social-login'
import { useLocalLogin, useLogin } from '@/lib/hooks/auth-handle'
import { cn } from '@/lib/utils'
import { authSchema, localSignInSchema, type AuthSchema, type LocalSignInSchema } from '@/models/auth/auth'
import { zodResolver } from '@hookform/resolvers/zod'
import { Link2, MailCheck } from 'lucide-react'
import Link from 'next/link'
import { useTranslations } from 'next-intl'
import { useSearchParams } from 'next/navigation'
import { type FormEvent, type ReactNode, useEffect, useState } from 'react'
import { useForm, type SubmitHandler } from 'react-hook-form'

type LoginFormContentProps = {
  /** Where to land after a successful sign-in (forwarded to `useLogin`). */
  returnTo?: string
  /** Replaces the default "Login to your account" heading (e.g. in the guest login modal). */
  header?: (state: { codeSent: boolean }) => ReactNode
  /**
   * Set when a social sign-in comes back here (/login page only): Kratos' `?flow=` to continue, or
   * an error id — Kratos' `?id=` or NextAuth's `?error=` (the session exchange failed).
   */
  kratosFlowId?: string | null
  kratosErrorId?: string | null
}

type LinkPrompt = Extract<SocialRedirect, { kind: 'link' }>
type SignInMode = 'magic' | 'password'

const SIGN_IN_ERROR_KEYS = {
  expired: 'errorExpired',
  invalid_code: 'errorInvalidCode',
  unavailable: 'errorUnavailable',
  rejected: 'errorRejected',
} as const satisfies Record<SignInErrorReason, string>

/** Translation key for a failed send/verify step; non-Kratos failures read as "unavailable". */
export const signInErrorKey = (error: unknown) =>
  SIGN_IN_ERROR_KEYS[error instanceof SignInCodeError ? error.reason : 'unavailable']

const modeTabClass = (active: boolean) =>
  cn(
    'flex-1 rounded-lg px-3 py-2 text-sm font-medium transition-colors',
    active ? 'bg-zinc-800 text-zinc-50' : 'text-zinc-400 hover:text-zinc-200',
  )

/**
 * Social (Google / Apple / Microsoft) + Kratos magic link + optional Strapi local password,
 * shared by `/login` and the guest login modal.
 */
export function LoginFormContent({ returnTo, header, kratosFlowId, kratosErrorId }: LoginFormContentProps) {
  const t = useTranslations('auth.login')
  const magicForm = useForm<AuthSchema>({
    resolver: zodResolver(authSchema),
    defaultValues: { email: '' },
  })
  const passwordForm = useForm<LocalSignInSchema>({
    resolver: zodResolver(localSignInSchema),
    defaultValues: { email: '', password: '' },
  })
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [pending, setPending] = useState<PendingSignIn | null>(null)
  const [code, setCode] = useState('')
  const [link, setLink] = useState<LinkPrompt | null>(null)
  const [mode, setMode] = useState<SignInMode>('magic')
  const { mutateAsync: login } = useLogin({ returnTo })
  const { mutateAsync: localLogin } = useLocalLogin({ returnTo })

  // Resume "check your inbox" if a sent link is still live (modal reopened, page reloaded).
  useEffect(() => setPending(getPendingSignIn()), [])

  // A social sign-in Kratos could not finish on its own comes back here (kratos.yml `ui_url`).
  useEffect(() => {
    if (!kratosFlowId && !kratosErrorId) return
    readSocialRedirect(kratosFlowId ?? null, kratosErrorId ?? null)
      .then((redirect) => {
        if (redirect?.kind === 'link') setLink(redirect)
        else if (redirect?.kind === 'error') setError(t('errorSocialFailed'))
      })
      .catch((err: unknown) => setError(t(signInErrorKey(err))))
  }, [kratosFlowId, kratosErrorId, t])

  const run = async (step: () => Promise<void>, onError?: (err: unknown) => string) => {
    setLoading(true)
    setError('')
    try {
      await step()
    } catch (err) {
      setError(onError ? onError(err) : t(signInErrorKey(err)))
    } finally {
      setLoading(false)
    }
  }

  const sendEmail = (email: string) =>
    run(async () => {
      setPending(await sendSignInEmail(email, returnTo))
      setCode('')
    })

  const onMagicSubmit: SubmitHandler<AuthSchema> = ({ email }) => sendEmail(email)

  const onPasswordSubmit: SubmitHandler<LocalSignInSchema> = (data) =>
    run(
      async () => {
        await localLogin(data)
      },
      (err) => (err instanceof Error && err.message === 'invalid_credentials' ? t('errorInvalidCredentials') : t('errorUnavailable')),
    )

  /** Chrome autofill can skip React onChange; read native values before Zod runs. */
  const submitPasswordForm = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    const form = event.currentTarget
    const email = form.querySelector<HTMLInputElement>('input[name="email"]')?.value ?? ''
    const password = form.querySelector<HTMLInputElement>('input[name="password"]')?.value ?? ''
    passwordForm.setValue('email', email.trim(), { shouldValidate: false })
    passwordForm.setValue('password', password, { shouldValidate: false })
    void passwordForm.handleSubmit(onPasswordSubmit)()
  }

  // Signing in on Kratos' link flow attaches the social account to the existing one.
  const sendLinkCode = (prompt: LinkPrompt) =>
    run(async () => {
      setPending(await sendSignInEmail(prompt.email, prompt.returnTo ?? returnTo, prompt.flow))
      setLink(null)
      setCode('')
    })

  const verifyCode = (value: string) =>
    run(async () => {
      await submitSignInCode(value, pending)
      await login(pending?.returnTo ?? returnTo)
    })

  const onVerify = (event: FormEvent) => {
    event.preventDefault()
    void verifyCode(code)
  }

  // Submit the moment the last digit lands (typed, pasted or OS autofill). Fired from the edit
  // itself rather than an effect, so a rejected code is not retried until the user changes it.
  const onCodeChange = (next: string) => {
    setCode(next)
    if (next.length === 6 && next !== code && !loading) void verifyCode(next)
  }

  const changeEmail = () => {
    clearPendingSignIn()
    setPending(null)
    setLink(null)
    setError('')
  }

  const providerName = link ? (PROVIDER_NAMES[link.provider as SocialProvider] ?? link.provider) : ''
  const switchMode = (next: SignInMode) => {
    setMode(next)
    setError('')
    if (next === 'password') {
      const email = magicForm.getValues('email')
      if (email) passwordForm.setValue('email', email)
    } else {
      const email = passwordForm.getValues('email')
      if (email) magicForm.setValue('email', email)
    }
  }

  return (
    <>
      {header ? header({ codeSent: !!pending }) : (
        <div className={authFormHeaderClass}>
          <h1 className="text-2xl font-semibold tracking-tight text-zinc-50">{t('title')}</h1>
          <p className="mt-2 text-sm text-zinc-400">{t('subtitle')}</p>
        </div>
      )}

      {error && (
        <div
          className="mb-4 rounded-md border border-red-500/40 bg-red-950/40 px-4 py-3 text-sm text-red-200"
          role="alert"
        >
          {error}
        </div>
      )}

      <div className={authFormStackClass}>
        {!pending && !link && (
          <>
            <SocialLoginButtons returnTo={returnTo} disabled={loading} />
            <AuthDivider label={t('orContinueWith')} />
          </>
        )}

        {pending ? (
          <form onSubmit={onVerify} className={authFormStackClass}>
            <div className="flex gap-3 rounded-xl border border-zinc-800 bg-zinc-900/60 p-4 text-sm">
              <span className="flex size-9 shrink-0 items-center justify-center rounded-full bg-brand-accent/15 text-brand-accent">
                <MailCheck className="size-4" aria-hidden />
              </span>
              <div className="min-w-0">
                <p className="font-medium text-zinc-50">{t('checkInboxTitle')}</p>
                <p className="mt-1 leading-relaxed text-zinc-400">
                  {t.rich('checkInboxBody', {
                    email: pending.email,
                    strong: (chunks) => <strong className="font-medium break-all text-zinc-100">{chunks}</strong>,
                  })}
                </p>
              </div>
            </div>

            <div className={authFormFieldClass}>
              <label htmlFor="sign-in-code" className={`text-sm font-medium ${authLabelClassName}`}>
                {t('codeLabel')}
              </label>
              <OtpCodeInput id="sign-in-code" value={code} onChange={onCodeChange} disabled={loading} autoFocus />
            </div>

            <Button
              type="submit"
              className={`w-full font-semibold ${authLargeControlClassName} ${authPrimaryButtonClassName}`}
              disabled={loading || code.length !== 6}
            >
              {t('verifyButton')}
            </Button>

            <div className="flex items-center justify-between gap-4 text-sm">
              <button
                type="button"
                onClick={changeEmail}
                disabled={loading}
                className="text-zinc-400 underline-offset-4 hover:text-zinc-200 hover:underline"
              >
                {t('useDifferentEmail')}
              </button>
              <button
                type="button"
                onClick={() => sendEmail(pending.email)}
                disabled={loading}
                className="text-zinc-400 underline-offset-4 hover:text-zinc-200 hover:underline"
              >
                {t('resendButton')}
              </button>
            </div>
          </form>
        ) : link ? (
          <div className={authFormStackClass}>
            <div className="flex gap-3 rounded-xl border border-zinc-800 bg-zinc-900/60 p-4 text-sm">
              <span className="flex size-9 shrink-0 items-center justify-center rounded-full bg-brand-accent/15 text-brand-accent">
                <Link2 className="size-4" aria-hidden />
              </span>
              <div className="min-w-0">
                <p className="font-medium text-zinc-50">{t('linkTitle', { provider: providerName })}</p>
                <p className="mt-1 leading-relaxed text-zinc-400">
                  {t.rich('linkBody', {
                    email: link.email,
                    provider: providerName,
                    strong: (chunks) => <strong className="font-medium break-all text-zinc-100">{chunks}</strong>,
                  })}
                </p>
              </div>
            </div>

            <Button
              type="button"
              onClick={() => void sendLinkCode(link)}
              className={`w-full font-semibold ${authLargeControlClassName} ${authPrimaryButtonClassName}`}
              disabled={loading}
            >
              {t('linkButton')}
            </Button>

            <button
              type="button"
              onClick={changeEmail}
              disabled={loading}
              className="text-sm text-zinc-400 underline-offset-4 hover:text-zinc-200 hover:underline"
            >
              {t('linkCancel')}
            </button>
          </div>
        ) : (
          <>
            <div className="flex gap-1 rounded-xl border border-zinc-800 bg-zinc-900/40 p-1" role="tablist" aria-label={t('title')}>
              <button type="button" role="tab" aria-selected={mode === 'magic'} className={modeTabClass(mode === 'magic')} onClick={() => switchMode('magic')}>
                {t('modeMagicLink')}
              </button>
              <button type="button" role="tab" aria-selected={mode === 'password'} className={modeTabClass(mode === 'password')} onClick={() => switchMode('password')}>
                {t('modePassword')}
              </button>
            </div>

            {mode === 'magic' ? (
              <Form {...magicForm}>
                <form onSubmit={magicForm.handleSubmit(onMagicSubmit)} className={authFormStackClass}>
                  <FormField
                    control={magicForm.control}
                    name="email"
                    render={({ field }) => (
                      <FormItem className={authFormFieldClass}>
                        <FormLabel className={authLabelClassName}>{t('emailLabel')}</FormLabel>
                        <FormControl>
                          <Input
                            type="email"
                            autoComplete="email"
                            placeholder={t('emailPlaceholder')}
                            className={`${authFieldClassName} ${authLargeControlClassName}`}
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <Button
                    type="submit"
                    className={`w-full font-semibold ${authLargeControlClassName} ${authPrimaryButtonClassName}`}
                    disabled={loading}
                  >
                    {t('sendLinkButton')}
                  </Button>
                </form>
              </Form>
            ) : (
              <Form {...passwordForm}>
                <form onSubmit={submitPasswordForm} className={authFormStackClass}>
                  <FormField
                    control={passwordForm.control}
                    name="email"
                    render={({ field }) => (
                      <FormItem className={authFormFieldClass}>
                        <FormLabel className={authLabelClassName}>{t('emailLabel')}</FormLabel>
                        <FormControl>
                          <Input
                            type="email"
                            autoComplete="email"
                            placeholder={t('emailPlaceholder')}
                            className={`${authFieldClassName} ${authLargeControlClassName}`}
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={passwordForm.control}
                    name="password"
                    render={({ field }) => (
                      <FormItem className={authFormFieldClass}>
                        <div className="flex items-center justify-between gap-2">
                          <FormLabel className={authLabelClassName}>{t('passwordLabel')}</FormLabel>
                          <Link href="/forgot-password" className="text-xs text-zinc-400 underline-offset-4 hover:text-zinc-200 hover:underline">
                            {t('forgotPasswordLink')}
                          </Link>
                        </div>
                        <FormControl>
                          <Input
                            type="password"
                            autoComplete="current-password"
                            placeholder={t('passwordPlaceholder')}
                            className={`${authFieldClassName} ${authLargeControlClassName}`}
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <Button
                    type="submit"
                    className={`w-full font-semibold ${authLargeControlClassName} ${authPrimaryButtonClassName}`}
                    disabled={loading}
                  >
                    {t('signInButton')}
                  </Button>
                </form>
              </Form>
            )}
          </>
        )}
      </div>
    </>
  )
}

const Login = () => {
  const tShell = useTranslations('auth.shell')
  const searchParams = useSearchParams()
  const returnTo = searchParams.get('returnTo') ?? undefined

  return (
    <AuthShell
      alternateAccount={{
        prompt: tShell('noAccountPrompt'),
        href: '/register',
        linkLabel: tShell('registerLink'),
      }}
    >
      <LoginFormContent
        returnTo={returnTo}
        kratosFlowId={searchParams.get('flow')}
        kratosErrorId={searchParams.get('id') ?? searchParams.get('error')}
      />
    </AuthShell>
  )
}

export default Login
